<template>
  <div class="xhs-viewer">
    <div class="xhs-phone">
      <div class="xhs-media">
        <video
          v-if="item.isVideo && item.videoUrl"
          class="xhs-video"
          :src="item.videoUrl"
          :poster="item.coverUrl || undefined"
          controls
          playsinline
        />
        <template v-else>
          <div v-if="slides.length" class="xhs-carousel">
            <img
              :src="slides[slideIndex]"
              class="xhs-slide"
              alt=""
              referrerpolicy="no-referrer"
              @error="onImgError"
            />
            <button
              v-if="slides.length > 1"
              type="button"
              class="xhs-nav xhs-nav--prev"
              @click="prev"
            >‹</button>
            <button
              v-if="slides.length > 1"
              type="button"
              class="xhs-nav xhs-nav--next"
              @click="next"
            >›</button>
            <div v-if="slides.length > 1" class="xhs-dots">
              <span
                v-for="(_, i) in slides"
                :key="i"
                class="xhs-dot"
                :class="{ active: i === slideIndex }"
              />
            </div>
            <div v-if="slides.length > 1" class="xhs-page">{{ slideIndex + 1 }}/{{ slides.length }}</div>
          </div>
          <div v-else class="xhs-media-empty">暂无图片</div>
        </template>
      </div>

      <div class="xhs-body">
        <div class="xhs-author">
          <div class="xhs-avatar">
            <img v-if="item.authorAvatar" :src="item.authorAvatar" alt="" referrerpolicy="no-referrer" />
            <span v-else>{{ (item.authorName || '?')[0] }}</span>
          </div>
          <div class="xhs-author-meta">
            <div class="xhs-author-name">{{ item.authorName || '作者' }}</div>
            <div v-if="item.publishedAt" class="xhs-time">{{ item.publishedAt }}</div>
          </div>
        </div>

        <h3 class="xhs-title">{{ item.title }}</h3>
        <p class="xhs-content">{{ item.content || '' }}</p>
        <div v-if="item.tags?.length" class="xhs-tags">
          <span v-for="(t, i) in item.tags" :key="i" class="xhs-tag">#{{ typeof t === 'string' ? t : (t.name || t) }}</span>
        </div>
      </div>

      <div class="xhs-bar">
        <span>♡ {{ formatCount(item.likes) }}</span>
        <span>★ {{ formatCount(item.collects) }}</span>
        <span>💬 {{ formatCount(item.comments) }}</span>
        <span>↗ {{ formatCount(item.shares) }}</span>
      </div>

      <XhsCommentSection v-if="item.platform === 'xiaohongshu'" :item="item" />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { formatCount } from './normalizeContent'
import XhsCommentSection from './XhsCommentSection.vue'

const props = defineProps({
  item: { type: Object, required: true },
})

const slideIndex = ref(0)
const slides = computed(() => {
  const list = props.item?.imageUrls?.length
    ? props.item.imageUrls
    : (props.item?.coverUrl ? [props.item.coverUrl] : [])
  return list
})

watch(slides, () => { slideIndex.value = 0 })

const prev = () => {
  const n = slides.value.length
  if (!n) return
  slideIndex.value = (slideIndex.value - 1 + n) % n
}
const next = () => {
  const n = slides.value.length
  if (!n) return
  slideIndex.value = (slideIndex.value + 1) % n
}
const onImgError = (e) => {
  e.target.style.opacity = '0.35'
}
</script>

<style scoped>
.xhs-viewer {
  display: flex;
  justify-content: center;
  padding: 8px 0 4px;
  background: linear-gradient(180deg, #fff5f7 0%, #f7f8fb 40%, #f7f8fb 100%);
}
.xhs-phone {
  width: min(360px, 100%);
  background: #fff;
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 12px 36px rgba(25, 38, 66, 0.12);
  border: 1px solid #f0e4e7;
}
.xhs-media {
  position: relative;
  background: #111;
  aspect-ratio: 3 / 4;
}
.xhs-video,
.xhs-slide {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background: #111;
}
.xhs-carousel {
  position: relative;
  width: 100%;
  height: 100%;
}
.xhs-media-empty {
  height: 100%;
  display: grid;
  place-items: center;
  color: #888;
  background: #1a1a1a;
}
.xhs-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}
.xhs-nav--prev { left: 8px; }
.xhs-nav--next { right: 8px; }
.xhs-dots {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 12px;
  display: flex;
  justify-content: center;
  gap: 5px;
}
.xhs-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.45);
}
.xhs-dot.active { background: #ff2442; }
.xhs-page {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 11px;
  color: #fff;
  background: rgba(0, 0, 0, 0.4);
  padding: 2px 8px;
  border-radius: 10px;
}
.xhs-body { padding: 14px 16px 8px; }
.xhs-author {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.xhs-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  background: #ffe1e7;
  color: #e84f69;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
}
.xhs-avatar img { width: 100%; height: 100%; object-fit: cover; }
.xhs-author-name { font-size: 14px; font-weight: 600; color: #333; }
.xhs-time { font-size: 11px; color: #999; margin-top: 2px; }
.xhs-title {
  margin: 0 0 8px;
  font-size: 16px;
  line-height: 1.45;
  color: #1a1a1a;
  font-weight: 650;
}
.xhs-content {
  margin: 0;
  white-space: pre-wrap;
  font-size: 13px;
  line-height: 1.7;
  color: #333;
}
.xhs-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}
.xhs-tag {
  font-size: 12px;
  color: #13386c;
}
.xhs-bar {
  display: flex;
  justify-content: space-around;
  padding: 12px 8px 14px;
  border-top: 1px solid #f3f3f3;
  color: #666;
  font-size: 12px;
}
</style>
